import React from "react";
import { ApiContext } from "../common/context/ApiContext";
import { useLiveData } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { useTranslation } from "react-i18next";
import { Row } from "react-bootstrap";
import { BackNav, FlexRow, PageContainer } from "../common/CommonStyles";
import { Link } from "react-router-dom";
import {
  SchoolGroupInfo,
  SchoolGroupList,
  SchoolGroupType,
  SCHOOL_GROUP_TYPE_LABELS,
} from "../common/api";

type SortKey = "num_schools" | "tutoring_value_dollars";

export const SchoolGroupListPage: React.FC = () => {
  const { t } = useTranslation();
  const { getSchoolGroupList } = React.useContext(ApiContext);
  const { accountLiveData } = React.useContext(AccountContext);
  const { setCurrentTab } = React.useContext(TabsContext);
  const [account, setAccount] = React.useState<Account>();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const listFetchedForUserVersion = React.useRef<number | null>(null);
  const fetchJobForUserVersion = React.useRef<number | null>(null);
  const [schoolGroupList, setSchoolGroupList] =
    React.useState<SchoolGroupList | null>(null);
  const [search, setSearch] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState<number | null>(null);
  const [sortKey, setSortKey] = React.useState<SortKey | null>(null);
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const indicator = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ▴" : " ▾") : "";

  React.useEffect(() => {
    setCurrentTab(TabEnum.SchoolGroupList);
  }, [setCurrentTab]);

  useLiveData(
    React.useCallback(async (account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  React.useEffect(() => {
    (async () => {
      if (!account) {
        return;
      }
      if (
        account.userCurrentVersion !== listFetchedForUserVersion.current &&
        account.userCurrentVersion !== fetchJobForUserVersion.current
      ) {
        startLoading();
        const expectedVersion = (fetchJobForUserVersion.current =
          account.userCurrentVersion);
        setSchoolGroupList(await getSchoolGroupList());
        if (expectedVersion !== fetchJobForUserVersion.current) {
          return;
        }
        listFetchedForUserVersion.current = fetchJobForUserVersion.current;
        stopLoading();
      }
    })();
  }, [account, getSchoolGroupList, startLoading, stopLoading]);

  if (!schoolGroupList) {
    return <></>;
  }

  const filteredGroups: SchoolGroupInfo[] =
    schoolGroupList.school_groups.filter((g) => {
      if (search && !g.name.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      if (typeFilter !== null && g.type !== typeFilter) {
        return false;
      }
      return true;
    });

  const sorted = sortKey
    ? [...filteredGroups].sort((a, b) => {
        const cmp = a[sortKey] - b[sortKey];
        return sortDir === "asc" ? cmp : -cmp;
      })
    : filteredGroups;

  return (
    <PageContainer>
      <Row className="align-items-center">
        <FlexRow>
          <BackNav label={t("generic_nav_back")} />
        </FlexRow>
      </Row>
      <div className="text-center mt-4 mb-3">
        <h3>{t("school_group_list_title")}</h3>
      </div>
      <div className="d-flex gap-2 mb-3">
        <input
          className="form-control"
          style={{ maxWidth: 300 }}
          type="text"
          placeholder={t("school_group_list_search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-select"
          style={{ maxWidth: 200 }}
          value={typeFilter === null ? "" : String(typeFilter)}
          onChange={(e) =>
            setTypeFilter(
              e.target.value === "" ? null : parseInt(e.target.value)
            )
          }
        >
          <option value="">{t("school_group_list_all_types")}</option>
          {(
            Object.values(SchoolGroupType).filter(
              (v) => typeof v === "number"
            ) as SchoolGroupType[]
          ).map((v) => (
            <option key={v} value={String(v)}>
              {SCHOOL_GROUP_TYPE_LABELS[v]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <table className="table align-middle">
          <thead>
            <tr>
              <th scope="col">{t("school_group_list_name")}</th>
              <th scope="col">{t("school_group_list_type")}</th>
              <th scope="col">{t("school_group_list_district")}</th>
              <th
                scope="col"
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("num_schools")}
              >
                {t("school_group_list_num_schools")}
                {indicator("num_schools")}
              </th>
              <th
                scope="col"
                className="text-end"
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("tutoring_value_dollars")}
              >
                {t("school_group_list_tutoring_value")}
                {indicator("tutoring_value_dollars")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((group, index) => (
              <tr key={index}>
                <td>
                  <Link
                    className="text-decoration-none"
                    to={`/schools/dashboard/?school_group_id=${group.id}`}
                  >
                    {group.name}
                  </Link>
                </td>
                <td>{SCHOOL_GROUP_TYPE_LABELS[group.type] ?? group.type}</td>
                <td>{group.district_name}</td>
                <td>{t("intcomma", { val: group.num_schools })}</td>
                <td className="text-end">
                  ${t("intcomma", { val: group.tutoring_value_dollars })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageContainer>
  );
};
